import { Component, OnInit, Output, Input, OnChanges, EventEmitter, ChangeDetectionStrategy, SimpleChanges, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { DocumentConfigService, DocumentConfigSearch } from './services/document-config.service';
import { Direction, DocumentConfig, DocumentConfigSearchDisplay, DocumentTypeEntity } from './models';
import { DocumentConfigLinksComponent } from './document-config-links.component';
import { FormsModule } from '@angular/forms';

@Component({
    templateUrl: './document-config-selector-simple.component.html',
    selector: 'ep-document-config-selector-simple',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DocumentConfigLinksComponent, FormsModule]
})
export class DocumentConfigSelectorSimpleComponent implements OnInit, OnChanges {
    private readonly documentConfigService = inject(DocumentConfigService);

    @Input() direction: Direction | null;
    @Input() distributionObject: DocumentTypeEntity;
    @Input() selectFirstItemIfOnlyOne = false;
    @Input() documentConfig: DocumentConfig = null;
    @Input() showAllOption = true;
    @Input() showAllOptionText = 'All';
    @Input() titleText = '';
    @Input() showConfigLinks = true;
    @Output() documentConfigChange: EventEmitter<DocumentConfig> = new EventEmitter<DocumentConfig>();

    initializedSubject = new Subject<void>();
    public initialized = this.initializedSubject.asObservable();
    public selectedDocumentConfig: DocumentConfigSearchDisplay = null;
    public documentConfigs: DocumentConfigSearchDisplay[];
    public searchObject: DocumentConfigSearch;
    private usingFakeDocumentConfig = false; // Indicates whether documentConfigs are set to a fake value that doesn't really exist and shouldn't be overwritten

    public ngOnInit(): void {
        this.initializeSearchObject();
        void this.search().then(() => {
            this.initializedSubject.next();
            this.initializedSubject.complete();
        });
    }

    initializeSearchObject(): void {
        if (this.searchObject) {
            this.searchObject.direction = this.direction;
        } else {
            this.searchObject = new DocumentConfigSearch(this.direction);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.distributionObject && this.distributionObject) {
            void this.refresh(this.distributionObject.Id);
        }
        if (changes.documentConfig && this.documentConfig) {
            this.setSelectedInternal(this.documentConfig.DocumentConfigsId, false);
        }
    }

    public reset(): Promise<void> {
        this.documentConfig = null;
        return this.refresh(null);
    }

    public refresh(distributionObjectId: number | null = null): Promise<void> {
        this.initializeSearchObject();
        if (this.searchObject) { // Only available when init has been called
            this.searchObject.distributionObjectId = distributionObjectId;
            return this.search();
        }
        return Promise.resolve();
    }

    public setSelected(documentConfigId: number): void {
        this.setSelectedInternal(documentConfigId, false);
    }

    setSelectedInternal(documentConfigId: number, emitChange: boolean): void {
        if (this.documentConfigs) {
            this.selectedDocumentConfig = this.documentConfigs.find((dt) => dt.DocumentConfigId === documentConfigId) || null;
            if (this.selectedDocumentConfig) {
                void this.documentConfigService.getDocumentConfig(this.selectedDocumentConfig.DocumentConfigId).then(result => {
                    if (emitChange) {
                        this.emitInternal(result);
                    }
                });
            }
            else {
                this.emitInternal(null);
            }
        }
    }

    emitInternal(documentConfig: DocumentConfig): void {
        this.documentConfig = documentConfig;
        this.documentConfigChange.emit(this.documentConfig);
    }

    async search() {
        if (!this.usingFakeDocumentConfig) {
            let result: DocumentConfigSearchDisplay[] = await this.documentConfigService.getDocumentConfigs(this.searchObject);
            result = result.map(item => {
                const distributionObjectSuffix = this.searchObject.distributionObjectId ? '' : ` [${item.DistributionObjectName}]`;
                item.DisplayName = `${item.FormatName} / ${item.VariantName} / ${item.VersionName}${distributionObjectSuffix}`;
                return item;
            });
            result.sort((a, b) => (a.DisplayName ?? '').localeCompare(b.DisplayName ?? ''));

            if (!this.usingFakeDocumentConfig) { // Necessary because of timing issues with asynchronous search results
                this.documentConfigs = result;
                if (this.documentConfig && this.documentConfig.DocumentConfigsId) {
                    this.setSelected(this.documentConfig.DocumentConfigsId);
                }
                else if (this.selectFirstItemIfOnlyOne && this.documentConfigs.length === 1) {
                    this.setSelectedInternal(this.documentConfigs[0].DocumentConfigId, true);
                }
            }
        }
    };

    setSingleDocumentConfig = (documentConfig: DocumentConfigSearchDisplay) => {
        this.usingFakeDocumentConfig = true;
        this.showAllOption = false;
        this.documentConfigs = [documentConfig];
        this.selectedDocumentConfig = documentConfig;
    };

    documentConfigChanged(documentConfig: DocumentConfigSearchDisplay): void {
        if (documentConfig) {
            this.setSelectedInternal(documentConfig.DocumentConfigId, true);
        }
        else {
            this.setSelectedInternal(-1, true);
        }
    }
}
